/** Accepted PULSE projection v1 only. No free text, URLs or inferred provenance. */
export interface PulseSubject {
    type: "company" | "contact";
    input: string;
    key: string;
    hash: string;
}
export interface PulseQuote {
    id: string;
    credits: string;
    expiresAt: string;
}
export interface PulseBinding {
    subject: PulseSubject;
    quoteId: string;
}
export interface PulseJob {
    id: string;
    status: string;
    marker: string | null;
}
export interface PulseField {
    field_id: "domain" | "employee_count";
    value: string | number;
    source_class: string | null;
}
export interface PulseEvidence {
    state: "pending" | "available" | "unavailable" | "failed";
    outcome: "success" | "partial" | null;
    marker: string | null;
    completedAt: string | null;
    evidence: PulseField[];
    limitations: string[];
}
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MARKERS = [null, "pulse.pending", "pulse.enriched", "pulse.enriched.unbilled", "pulse.failed"];
const SOURCES = ["business contact directory record", "company directory record", "contact verification service", "domain registration record", "language model route", "open-source research brief", "person directory record", "professional network record", "public social record", "public web record", "search data record", "search result lookup", "search visibility record", "site technology scan", "verified email lookup", "website capture", "provided", "domain_cache"];
const LIMITATIONS = ["unsupported_field", "malformed_field", "unsafe_text", "unsafe_source_url", "unknown_source_class", "unsupported_contact_evidence", "source_observed_at_unknown", "evidence_version_unknown", "missing_completed_at", "evidence_limit_reached"];
function invalid(message = "PULSE response unavailable"): never { throw new Error(message); }
function record(v: unknown): Record<string, unknown> { if (!v || typeof v !== "object" || Array.isArray(v))
    return invalid(); return v as Record<string, unknown>; }
function uuid(v: unknown): string { if (typeof v !== "string" || !UUID.test(v))
    return invalid(); return v; }
function date(v: unknown): string { if (typeof v !== "string" || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?(?:Z|[+-]\d{2}:\d{2})$/.test(v) || !Number.isFinite(Date.parse(v)))
    return invalid(); if ((v.endsWith("Z") || v.endsWith("+00:00")) && new Date(v).toISOString().slice(0, 19) !== v.slice(0, 19))
    return invalid(); return v; }
export function canonicalDomain(raw: string): string {
    let v = raw.trim().toLowerCase().replace(/^[a-z][a-z0-9+.-]*:\/\//, "");
    if (v.includes("@"))
        return invalid("Invalid subject");
    v = v.split(/[/?#]/)[0]!.split(":")[0]!.replace(/\.$/, "").replace(/^www\./, "");
    const labels = v.split(".");
    if (!v || v.length > 253 || v.startsWith("[") || /^\d{1,3}(\.\d{1,3}){3}$/.test(v) || labels.length < 2 || labels.at(-1)!.length < 2 || labels.some(l => !l || l.length > 63 || !/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(l)))
        return invalid("Invalid subject");
    return v;
}
async function hash(v: string): Promise<string> { const bytes = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(v)); return Array.from(new Uint8Array(bytes), b => b.toString(16).padStart(2, "0")).join(""); }
export async function canonicalSubject(raw: {
    type: "company" | "contact";
    key: string;
}): Promise<PulseSubject> {
    const input = raw.type === "company" ? canonicalDomain(raw.key) : raw.key.trim().toLowerCase();
    if (raw.type === "contact") {
        const m = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(.+)$/.exec(input);
        if (!m || input.length > 320)
            return invalid("Invalid subject");
        canonicalDomain(m[1]!);
    }
    return { type: raw.type, input, key: raw.type === "contact" ? await hash(input) : input, hash: await hash(`${raw.type}:${input}`) };
}
export function parseQuote(raw: unknown, s: PulseSubject, org: string, now: number): PulseQuote {
    const q = record(raw);
    const id = uuid(q.id);
    uuid(q.rate_card_id);
    date(q.created_at);
    const expiresAt = date(q.expires_at);
    if (q.org_id !== org || q.action !== "enrich_context" || q.unit_count !== 1 || q.payload_hash !== s.hash || q.status !== "open" || q.consumed_at !== null)
        return invalid();
    const credits = typeof q.credits === "number" && Number.isFinite(q.credits) ? String(q.credits) : q.credits;
    if (typeof credits !== "string" || !/^\d+(?:\.\d+)?$/.test(credits) || !Number.isFinite(Number(credits)))
        return invalid();
    if (Date.parse(expiresAt) <= now)
        return invalid("Quote expired");
    return { id, credits, expiresAt };
}
function binding(raw: unknown, a: PulseBinding): void { const b = record(raw), s = record(b.subject); if (s.type !== a.subject.type || s.key !== a.subject.key || b.quote_id !== a.quoteId)
    return invalid(); }
export function parseAck(raw: unknown, a: PulseBinding): string { const r = record(raw), s = record(r.subject); if (r.status !== "queued" || s.type !== a.subject.type || s.key !== a.subject.key)
    return invalid(); return uuid(r.job_id); }
export function parseJob(raw: unknown, id?: string): PulseJob {
    const r = record(raw);
    const jobId = uuid(r.id);
    if (id && jobId !== id)
        return invalid();
    if (r.kind !== "pulse.enrich_context" || !["queued", "running", "succeeded", "failed", "cancelled"].includes(String(r.status)) || typeof r.progress !== "number" || !Number.isFinite(r.progress) || r.progress < 0 || r.progress > 100 || !MARKERS.includes(r.result_ref as string | null))
        return invalid();
    return { id: jobId, status: r.status as string, marker: r.result_ref as string | null };
}
export function parseRecovery(raw: unknown, a: PulseBinding): PulseJob | null {
    const r = record(raw), p = record(r.pagination);
    if (!Array.isArray(r.jobs) || r.jobs.length > 1 || p.offset !== 0 || p.limit !== 1 || p.total !== r.jobs.length)
        return invalid();
    if (!r.jobs.length)
        return null;
    const j = record(r.jobs[0]);
    binding(j.binding, a);
    return parseJob(j);
}
export function parseEvidence(raw: unknown, a: PulseBinding, jobId: string): PulseEvidence {
    const r = record(raw);
    if (r.projection_version !== 1 || r.job_id !== jobId || r.kind !== "pulse.enrich_context" || !MARKERS.includes(r.delivery_marker as string | null) || !["pending", "available", "unavailable", "failed"].includes(String(r.result_state)))
        return invalid();
    binding(r.binding, a);
    if (r.outcome !== null && r.outcome !== "success" && r.outcome !== "partial")
        return invalid();
    if (r.request_id !== null && r.request_id !== jobId)
        return invalid();
    const completedAt = r.completed_at === null ? null : date(r.completed_at);
    if (!Array.isArray(r.evidence) || r.evidence.length > 32 || !Array.isArray(r.limitations) || r.limitations.some(x => !LIMITATIONS.includes(x)))
        return invalid();
    if (r.result_state === "available" && (r.request_id !== jobId || !["pulse.enriched", "pulse.enriched.unbilled"].includes(String(r.delivery_marker)) || r.outcome === null))
        return invalid();
    if (r.result_state !== "available" && (r.evidence.length || r.outcome !== null))
        return invalid();
    const evidence = r.evidence.map(rawField => {
        const f = record(rawField);
        if (a.subject.type !== "company" || f.kind !== "field" || f.source_url !== null || f.observed_at !== null || f.evidence_version !== null || (f.source_class !== null && !SOURCES.includes(String(f.source_class))))
            return invalid();
        if (f.field_id === "domain") {
            if (f.value !== a.subject.key)
                return invalid();
        }
        else if (f.field_id === "employee_count") {
            if (typeof f.value !== "number" || !Number.isFinite(f.value) || f.value < 0)
                return invalid();
        }
        else
            return invalid();
        return { field_id: f.field_id, value: f.value, source_class: f.source_class } as PulseField;
    });
    return { state: r.result_state as PulseEvidence["state"], outcome: r.outcome as PulseEvidence["outcome"], marker: r.delivery_marker as string | null, completedAt, evidence, limitations: r.limitations as string[] };
}
